import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { GoogleMap, useLoadScript } from "@react-google-maps/api"
import {
  defaultLat,
  defaultLng,
  googleAPIKeys,
  GOOGLE_MAPS_LIBRARIES,
} from "common/utils"
import { toLatLngLiteral, toLatLngPaths } from "common/geoBoundaryUtils"
import { createZoneMapLabelOverlayFromPaths } from "common/zoneMapLabelOverlay"
import { useTranslation } from "react-i18next"

const mapContainerStyle = { width: "100%", height: "350px" }

const RegionBoundaryView = ({ boundary, zoneLabel = "" }) => {
  const { t } = useTranslation()
  const mapRef = useRef(null)
  const polygonRef = useRef(null)
  const zoneLabelOverlayRef = useRef(null)
  const [mapReady, setMapReady] = useState(false)

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleAPIKeys,
    libraries: GOOGLE_MAPS_LIBRARIES,
  })

  const paths = useMemo(
    () => toLatLngPaths(boundary?.coordinates?.[0]),
    [boundary]
  )
  const hasBoundary = paths.length > 0

  const clearPolygon = useCallback(() => {
    if (polygonRef.current) {
      polygonRef.current.setMap(null)
      polygonRef.current = null
    }
    if (zoneLabelOverlayRef.current) {
      zoneLabelOverlayRef.current.setMap(null)
      zoneLabelOverlayRef.current = null
    }
  }, [])

  const drawBoundary = useCallback(
    (map, latLngPaths, label = zoneLabel) => {
      if (!map || !latLngPaths?.length) {
        return
      }

      clearPolygon()

      polygonRef.current = new window.google.maps.Polygon({
        paths: latLngPaths,
        editable: false,
        draggable: false,
        map,
        strokeColor: "#1976d2",
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: "#1976d2",
        fillOpacity: 0.2,
      })

      const labelOverlay = createZoneMapLabelOverlayFromPaths(
        map,
        latLngPaths,
        label,
        "#1976d2"
      )
      if (labelOverlay) {
        zoneLabelOverlayRef.current = labelOverlay
      }

      const bounds = new window.google.maps.LatLngBounds()
      latLngPaths.forEach(point => {
        const literal = toLatLngLiteral(point)
        if (literal) bounds.extend(literal)
      })
      map.fitBounds(bounds)
    },
    [clearPolygon, zoneLabel]
  )

  useEffect(() => {
    if (!isLoaded || !mapReady || !mapRef.current || !hasBoundary) {
      return
    }

    drawBoundary(mapRef.current, paths)

    return clearPolygon
  }, [isLoaded, mapReady, hasBoundary, paths, drawBoundary, clearPolygon])

  if (!hasBoundary) {
    return <p className="text-muted mb-0">{t("No boundary defined")}</p>
  }

  return (
    <div style={mapContainerStyle}>
      {!isLoaded ? (
        <div className="d-flex align-items-center justify-content-center h-100">
          {t("Loading")}...
        </div>
      ) : (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={{ lat: defaultLat, lng: defaultLng }}
          zoom={10}
          onLoad={map => {
            mapRef.current = map
            setMapReady(true)
          }}
          options={{
            draggable: true,
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: true,
          }}
        />
      )}
    </div>
  )
}

export default RegionBoundaryView
